import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
  writeBatch
} from 'firebase/firestore'
import { db } from './firebase'

// Colecciones
const USERS_COLLECTION = 'users'
const ROLES_COLLECTION = 'roles'
const ROLE_ASSIGNMENTS_COLLECTION = 'roleAssignments'
const INVITATION_CODES_COLLECTION = 'invitationCodes'
const PERMISSION_CACHE_COLLECTION = 'permissionCache'

// **Niveles Jerarquicos (Cuanto mas alto, mas poder)**
// SOLO DOS ROLES: ADMIN y USER
export const USER_ROLES = {
  ADMIN: 100,           // Administrador - acceso total
  USER: 10,             // Usuario normal - acceso básico
  GUEST: 0              // No autenticado
}

// **Nombres y Metadatos de Roles**
export const ROLE_METADATA = {
  [USER_ROLES.ADMIN]: {
    name: 'Admin',
    displayName: 'Administrador',
    color: '#FF8C42',
    permissions: [
      'users.manage', 'content.moderate', 'events.manage',
      'groups.manage', 'announcements.create', 'system.config',
      'reports.manage', 'moderation.admin'
    ],
    description: 'Acceso total a la administración'
  },
  [USER_ROLES.USER]: {
    name: 'User',
    displayName: 'Usuario',
    color: '#3BC6C6',
    permissions: [
      'content.create', 'events.create', 'groups.join',
      'chat.participate', 'profile.manage'
    ],
    description: 'Usuario estándar con acceso básico'
  },
  [USER_ROLES.GUEST]: {
    name: 'Guest',
    displayName: 'Visitante',
    color: '#A0AEC0',
    permissions: ['view.public'],
    description: 'Usuario no autenticado con acceso limitado'
  }
}

// 🎯 **Lista de Permisos Específicos**
export const PERMISSIONS = {
  // Gestión de usuarios
  'users.manage': 'Gestionar todos los usuarios',
  'users.moderate': 'Moderar usuarios (ban/mute)',
  'users.promote': 'Promover usuarios',

  // Contenido
  'content.moderate': 'Moderar cualquier contenido',
  'content.create': 'Crear contenido básico',
  'content.create_unlimited': 'Crear contenido sin límites',
  'content.announcements': 'Crear anuncios importantes',

  // Eventos
  'events.manage': 'Gestionar todos los eventos',
  'events.moderate': 'Moderar eventos',
  'events.create': 'Crear eventos básicos',
  'events.create_advanced': 'Crear eventos avanzados',
  'events.create_unlimited': 'Crear eventos sin límites',

  // Grupos
  'groups.manage': 'Gestionar todos los grupos',
  'groups.moderate': 'Moderar grupos',
  'groups.create': 'Crear grupos básicos',
  'groups.create_advanced': 'Crear grupos avanzados',
  'groups.join': 'Unirse a grupos',

  // Chat
  'chat.moderate': 'Moderar chats',
  'chat.create_private': 'Crear chats privados',
  'chat.participate': 'Participar en chats',

  // Sistema
  'system.config': 'Configurar sistema',
  'system.analytics': 'Ver analytics del sistema',
  'announcements.create': 'Crear anuncios globales',
  'analytics.view': 'Ver analytics básicos',

  // Admin especial
  '*': 'Todos los permisos'
}

// ✅ **Obtener rol principal de un usuario**
export const getUserPrimaryRole = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
    if (!userDoc.exists()) {
      return USER_ROLES.USER // Default role
    }

    const userData = userDoc.data()
    return userData.primaryRole || USER_ROLES.USER
  } catch (error) {
    console.error('❌ Error getting user primary role:', error)
    return USER_ROLES.USER
  }
}

// ✅ **Obtener todos los roles de un usuario**
export const getUserRoles = async (userId) => {
  try {
    // SECURITY: Validar que userId existe y es válido antes de hacer query a Firestore
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      console.warn('⚠️ getUserRoles: userId inválido o no proporcionado')
      return [USER_ROLES.USER]
    }

    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
    if (!userDoc.exists()) {
      return [USER_ROLES.USER]
    }

    const userData = userDoc.data()
    const primaryRole = userData.primaryRole || USER_ROLES.USER
    const additionalRoles = userData.additionalRoles || []

    return [primaryRole, ...additionalRoles]
  } catch (error) {
    console.error('❌ Error getting user roles:', error)
    return [USER_ROLES.USER]
  }
}

// **Asignar rol a usuario**
export const assignRole = async (userId, roleLevel, assignedBy = null, reason = '') => {
  try {
    // Validar que el rol exista
    if (!Object.values(USER_ROLES).includes(roleLevel)) {
      throw new Error('Rol invalido')
    }

    // Obtener datos del usuario
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado')
    }

    const userData = userDoc.data()
    const currentRole = userData.primaryRole || USER_ROLES.USER

    // Solo permitir upgrade de rol (no downgrade) excepto para admins
    if (roleLevel < currentRole && assignedBy) {
      const assignerRoles = await getUserRoles(assignedBy)
      const maxAssignerRole = Math.max(...assignerRoles)

      // Solo ADMIN puede hacer downgrade
      if (maxAssignerRole < USER_ROLES.ADMIN) {
        throw new Error('No tienes permisos para hacer downgrade de este rol')
      }
    }

    // Actualizar rol principal
    const userRef = doc(db, USERS_COLLECTION, userId)
    await updateDoc(userRef, {
      primaryRole: roleLevel,
      roleAssignedAt: serverTimestamp(),
      roleAssignedBy: assignedBy,
      roleAssignmentReason: reason,
      roleHistory: arrayUnion({
        from: currentRole,
        to: roleLevel,
        assignedBy,
        reason,
        timestamp: serverTimestamp()
      })
    })

    // Registrar asignacion
    const assignmentRef = doc(collection(db, ROLE_ASSIGNMENTS_COLLECTION))
    await setDoc(assignmentRef, {
      userId,
      role: roleLevel,
      type: 'primary',
      assignedBy,
      reason,
      timestamp: serverTimestamp()
    })

    // Invalidar cache de permisos
    await invalidatePermissionCache(userId)

    console.log(`Role ${roleLevel} assigned to user ${userId}`)
    return true
  } catch (error) {
    console.error('Error assigning role:', error)
    throw error
  }
}

// ✅ **Añadir rol adicional a usuario**
export const addAdditionalRole = async (userId, roleLevel, assignedBy = null, reason = '') => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId)
    await updateDoc(userRef, {
      additionalRoles: arrayUnion(roleLevel),
      updatedAt: serverTimestamp()
    })

    // Registrar asignación
    const assignmentRef = doc(collection(db, ROLE_ASSIGNMENTS_COLLECTION))
    await setDoc(assignmentRef, {
      userId,
      role: roleLevel,
      type: 'additional',
      assignedBy,
      reason,
      timestamp: serverTimestamp()
    })

    // Invalidar caché
    await invalidatePermissionCache(userId)

    console.log(`✅ Additional role ${roleLevel} added to user ${userId}`)
    return true
  } catch (error) {
    console.error('❌ Error adding additional role:', error)
    throw error
  }
}

// **Verificar si usuario tiene permiso especifico**
export const hasPermission = async (userId, permission) => {
  try {
    // SECURITY: Validar que userId existe y es válido
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      console.warn('⚠️ hasPermission: userId inválido o no proporcionado')
      return false
    }

    // Verificar cache primero
    const cached = await getCachedPermission(userId, permission)
    if (cached !== null) {
      return cached
    }

    const userRoles = await getUserRoles(userId)
    const maxRole = Math.max(...userRoles)

    // Admin tiene todos los permisos
    if (maxRole >= USER_ROLES.ADMIN) {
      await cachePermission(userId, permission, true)
      return true
    }

    // Verificar permisos especificos de cada rol
    for (const role of userRoles) {
      const roleMeta = ROLE_METADATA[role]
      if (roleMeta) {
        // Si tiene permiso comodin
        if (roleMeta.permissions.includes('*')) {
          await cachePermission(userId, permission, true)
          return true
        }

        // Si tiene el permiso especifico
        if (roleMeta.permissions.includes(permission)) {
          await cachePermission(userId, permission, true)
          return true
        }
      }
    }

    // Permisos base para todos los usuarios autenticados
    if (maxRole >= USER_ROLES.USER) {
      const basePermissions = [
        'content.create', 'events.create', 'groups.join',
        'chat.participate', 'profile.manage'
      ]
      if (basePermissions.includes(permission)) {
        await cachePermission(userId, permission, true)
        return true
      }
    }

    await cachePermission(userId, permission, false)
    return false
  } catch (error) {
    console.error('Error checking permission:', error)
    return false
  }
}

// ✅ **Verificar nivel mínimo de rol**
export const hasMinimumRole = async (userId, minimumRole) => {
  try {
    // SECURITY: Validar que userId existe y es válido
    if (!userId || typeof userId !== 'string' || userId.trim() === '') {
      console.warn('⚠️ hasMinimumRole: userId inválido o no proporcionado')
      return false
    }

    const userRoles = await getUserRoles(userId)
    const maxRole = Math.max(...userRoles)
    return maxRole >= minimumRole
  } catch (error) {
    console.error('❌ Error checking minimum role:', error)
    return false
  }
}

// ✅ **Obtener metadata del rol de un usuario**
export const getUserRoleMetadata = async (userId) => {
  try {
    const roles = await getUserRoles(userId)
    const maxRole = Math.max(...roles)
    return ROLE_METADATA[maxRole] || ROLE_METADATA[USER_ROLES.USER]
  } catch (error) {
    console.error('❌ Error getting user role metadata:', error)
    return ROLE_METADATA[USER_ROLES.USER]
  }
}

// 🎟️ **SISTEMA DE INVITATION CODES (Mejorado)**

// ✅ **Generar código de invitación**
export const generateInvitationCode = async (options = {}) => {
  try {
    const {
      role = USER_ROLES.USER,          // Rol que otorgará
      maxUses = 1,                     // Máximo usos
      expiresAt = null,                // Fecha de expiración
      createdBy = null,                // Quién lo creó
      description = '',                 // Descripción
      restrictions = {}               // Restricciones adicionales
    } = options

    // Generar código numérico único de 10 dígitos
    const code = generateNumericCode(10)

    const invitationRef = doc(collection(db, INVITATION_CODES_COLLECTION))
    await setDoc(invitationRef, {
      code,
      role,
      level: role, // El nivel es igual al valor del rol (ADMIN=100, USER=10)
      maxUses,
      currentUses: 0,
      expiresAt: expiresAt ? new Date(expiresAt) : null,
      createdBy,
      description,
      restrictions,
      isActive: true,
      createdAt: serverTimestamp()
    })

    console.log(`✅ Invitation code generated: ${code}`)
    return code
  } catch (error) {
    console.error('❌ Error generating invitation code:', error)
    throw error
  }
}

// ✅ **Validar y usar código de invitación**
export const validateInvitationCode = async (code, userId = null) => {
  try {
    const codesQuery = query(
      collection(db, INVITATION_CODES_COLLECTION),
      where('code', '==', code),
      where('isActive', '==', true)
    )

    const querySnapshot = await getDocs(codesQuery)
    if (querySnapshot.empty) {
      return { valid: false, reason: 'Código no encontrado o inactivo' }
    }

    const invitationDoc = querySnapshot.docs[0]
    const invitationData = invitationDoc.data()

    // NOTA: Los códigos ya no tienen expiración - son permanentes
    // Los roles solo pueden ser revocados por administradores superiores

    // Verificar usos máximos
    if (invitationData.currentUses >= invitationData.maxUses) {
      return { valid: false, reason: 'Código ya no tiene usos disponibles' }
    }

    // Verificar restricciones si hay usuario
    if (userId && invitationData.restrictions) {
      const { domainRestriction, emailWhitelist } = invitationData.restrictions

      if (domainRestriction) {
        const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
        if (userDoc.exists()) {
          const userEmail = userDoc.data().email
          const userDomain = userEmail.split('@')[1]
          if (userDomain !== domainRestriction) {
            return { valid: false, reason: 'Dominio no autorizado para este código' }
          }
        }
      }

      if (emailWhitelist && emailWhitelist.length > 0) {
        const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
        if (userDoc.exists()) {
          const userEmail = userDoc.data().email
          if (!emailWhitelist.includes(userEmail)) {
            return { valid: false, reason: 'Email no autorizado para este código' }
          }
        }
      }
    }

    return {
      valid: true,
      invitation: {
        id: invitationDoc.id,
        role: invitationData.role,
        maxUses: invitationData.maxUses,
        currentUses: invitationData.currentUses,
        description: invitationData.description
      }
    }
  } catch (error) {
    console.error('❌ Error validating invitation code:', error)
    return { valid: false, reason: 'Error al validar código' }
  }
}

// ✅ **Usar código de invitación (asignar rol)**
export const useInvitationCode = async (code, userId) => {
  try {
    const validation = await validateInvitationCode(code, userId)
    if (!validation.valid) {
      throw new Error(validation.reason)
    }

    const { invitation } = validation

    // Asignar rol al usuario
    await assignRole(userId, invitation.role, null, `Invitation code: ${code}`)

    // Actualizar contador de usos del código
    const invitationRef = doc(db, INVITATION_CODES_COLLECTION, invitation.id)
    await updateDoc(invitationRef, {
      currentUses: invitation.currentUses + 1,
      lastUsedAt: serverTimestamp(),
      lastUsedBy: userId
    })

    // Si se alcanzó el máximo de usos, desactivar
    if (invitation.currentUses + 1 >= invitation.maxUses) {
      await updateDoc(invitationRef, {
        isActive: false
      })
    }

    console.log(`✅ Invitation code ${code} used by user ${userId}`)
    return true
  } catch (error) {
    console.error('❌ Error using invitation code:', error)
    throw error
  }
}

// 🔧 **FUNCIONES AUXILIARES**

function generateNumericCode(length) {
  let result = ''
  for (let i = 0; i < length; i++) {
    // Asegurar que el primer dígito no sea 0 para evitar códigos que comiencen con 0
    if (i === 0) {
      result += Math.floor(Math.random() * 9) + 1 // Digits 1-9
    } else {
      result += Math.floor(Math.random() * 10) // Digits 0-9
    }
  }
  return result
}

function generateRandomCode(length) {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789'
  let result = ''
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return result
}

// ⚡ **CACHE DE PERMISOS (para optimización)**

async function cachePermission(userId, permission, value) {
  try {
    const cacheRef = doc(db, PERMISSION_CACHE_COLLECTION, `${userId}_${permission}`)
    await setDoc(cacheRef, {
      userId,
      permission,
      value,
      cachedAt: serverTimestamp(),
      expiresAt: new Date(Date.now() + 5 * 60 * 1000) // 5 minutos
    })
  } catch (error) {
    console.error('Error caching permission:', error)
  }
}

async function getCachedPermission(userId, permission) {
  try {
    const cacheRef = doc(db, PERMISSION_CACHE_COLLECTION, `${userId}_${permission}`)
    const cacheDoc = await getDoc(cacheRef)

    if (!cacheDoc.exists()) {
      return null
    }

    const cacheData = cacheDoc.data()

    // Verificar si el caché expiró
    if (cacheData.expiresAt && cacheData.expiresAt.toDate() < new Date()) {
      await deleteDoc(cacheRef)
      return null
    }

    return cacheData.value
  } catch (error) {
    console.error('Error getting cached permission:', error)
    return null
  }
}

async function invalidatePermissionCache(userId) {
  try {
    const cacheQuery = query(
      collection(db, PERMISSION_CACHE_COLLECTION),
      where('userId', '==', userId)
    )

    const querySnapshot = await getDocs(cacheQuery)
    const batch = writeBatch(db)

    querySnapshot.forEach((doc) => {
      batch.delete(doc.ref)
    })

    await batch.commit()
  } catch (error) {
    console.error('Error invalidating permission cache:', error)
  }
}

// 📊 **FUNCIONES DE REPORTING**

// ✅ **Obtener estadísticas de roles**
export const getRoleStatistics = async () => {
  try {
    const usersSnapshot = await getDocs(collection(db, USERS_COLLECTION))
    const roleStats = {}

    // Inicializar contadores
    Object.values(USER_ROLES).forEach(role => {
      roleStats[role] = 0
    })

    usersSnapshot.forEach((doc) => {
      const userData = doc.data()
      const role = userData.primaryRole || USER_ROLES.USER
      roleStats[role] = (roleStats[role] || 0) + 1
    })

    return roleStats
  } catch (error) {
    console.error('❌ Error getting role statistics:', error)
    throw error
  }
}

// ✅ **Obtener historial de asignaciones de roles**
export const getRoleAssignmentHistory = async (userId = null, limitCount = 50) => {
  try {
    let assignmentsQuery = query(
      collection(db, ROLE_ASSIGNMENTS_COLLECTION),
      orderBy('timestamp', 'desc'),
      limit(limitCount)
    )

    if (userId) {
      assignmentsQuery = query(
        collection(db, ROLE_ASSIGNMENTS_COLLECTION),
        where('userId', '==', userId),
        orderBy('timestamp', 'desc'),
        limit(limitCount)
      )
    }

    const querySnapshot = await getDocs(assignmentsQuery)
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      timestamp: doc.data().timestamp?.toDate()
    }))
  } catch (error) {
    console.error('❌ Error getting role assignment history:', error)
    throw error
  }
}

// ============================================
// 🚀 CUSTOM CLAIMS SUPPORT (Nueva Arquitectura)
// ============================================

/**
 * Assign Custom Claims to a user using Admin SDK
 * NOTE: This requires calling the admin script directly as it needs elevated privileges
 *
 * USO: Ejecutar el script admin directamente:
 * node scripts/set-custom-claims.js <uid> <role>
 *
 * @param {string} userId - User ID to assign claims
 * @param {string} roleName - Role name (ADMIN, MODERATOR, etc.)
 * @returns {Promise<boolean>} Success status
 */
export const setUserClaims = async (userId, roleName) => {
  try {
    // NOTA: Esta función NO puede ejecutarse desde el cliente
    // Requiere Firebase Admin SDK con service account
    //
    // Para asignar Custom Claims:
    // 1. Ejecutar: node scripts/set-custom-claims.js <uid> <role>
    // 2. O usar Cloud Functions si está disponible
    //
    // Esta función es un placeholder para documentación

    console.warn(`⚠️ setUserClaims requiere Admin SDK. Usa: node scripts/set-custom-claims.js ${userId} ${roleName}`)

    // Actualizar Firestore para mantener sincronización (backward compatibility)
    const roleLevel = Object.keys(USER_ROLES).find(key => key === roleName)?.value
    if (roleLevel) {
      await assignRole(userId, roleLevel, null, 'Custom Claims update')
    }

    return false
  } catch (error) {
    console.error('❌ Error setting user claims:', error)
    throw error
  }
}

/**
 * Refresh user claims from Firebase ID token
 * This should be called after role changes to immediately update permissions
 *
 * @param {object} authContext - Auth context with forceTokenRefresh method
 * @returns {Promise<object|null>} Updated claims or null
 */
export const refreshUserClaims = async (authContext) => {
  try {
    if (!authContext?.forceTokenRefresh) {
      console.warn('⚠️ Auth context no tiene forceTokenRefresh method')
      return null
    }

    const newClaims = await authContext.forceTokenRefresh()
    return newClaims
  } catch (error) {
    console.error('❌ Error refreshing user claims:', error)
    return null
  }
}

/**
 * Check if user has Custom Claims enabled
 * @param {string} userId - User ID to check
 * @returns {Promise<boolean>} True if user has Custom Claims
 */
export const hasCustomClaims = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
    if (!userDoc.exists()) {
      return false
    }

    const userData = userDoc.data()
    return userData.customClaimsEnabled || false
  } catch (error) {
    console.error('❌ Error checking custom claims:', error)
    return false
  }
}

/**
 * Get role name from role level
 * @param {number} roleLevel - Role level
 * @returns {string} Role name
 */
export const getRoleName = (roleLevel) => {
  const entry = Object.entries(USER_ROLES).find(([key, value]) => value === roleLevel)
  return entry ? entry[0] : 'USER'
}

/**
 * Get role metadata from role level
 * @param {number} roleLevel - Role level
 * @returns {object} Role metadata
 */
export const getRoleMetadata = (roleLevel) => {
  return ROLE_METADATA[roleLevel] || ROLE_METADATA[USER_ROLES.USER]
}

/**
 * Assign role with Custom Claims (if available)
 * This function:
 * 1. Updates Firestore (backward compatibility)
 * 2. Returns instructions for setting Custom Claims
 *
 * @param {string} userId - User ID
 * @param {number} roleLevel - Role level to assign
 * @param {string} assignedBy - Admin user ID
 * @param {string} reason - Reason for role change
 * @returns {Promise<object>} Result with instructions
 */
export const assignRoleWithClaims = async (userId, roleLevel, assignedBy = null, reason = '') => {
  try {
    const roleName = getRoleName(roleLevel)

    // 1. Actualizar Firestore (backward compatibility)
    await assignRole(userId, roleLevel, assignedBy, reason)

    // 2. Retornar instrucciones para Custom Claims
    return {
      success: true,
      firestoreUpdated: true,
      customClaimsPending: true,
      instructions: {
        command: `node scripts/set-custom-claims.js ${userId} ${roleName}`,
        note: 'Ejecuta este comando para establecer los Custom Claims del usuario',
        skipIf: 'El usuario ya tiene Custom Claims activados'
      }
    }
  } catch (error) {
    console.error('❌ Error assigning role with claims:', error)
    throw error
  }
}

/**
 * Get user data with correct role from Firestore
 * This function reads the user's primaryRole directly from their document
 * ensuring the most accurate role information is returned
 *
 * @param {string} userId - User ID
 * @returns {Promise<object>} User data with correct role
 */
export const getUserDataWithCorrectRole = async (userId) => {
  try {
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
    if (!userDoc.exists()) {
      return null
    }

    const userData = userDoc.data()

    // Try primaryRole first, then fall back to role field, then default to USER
    const primaryRole = userData.primaryRole || userData.role || USER_ROLES.USER

    // Obtener metadata del rol
    const roleMetadata = ROLE_METADATA[primaryRole] || ROLE_METADATA[USER_ROLES.USER]

    return {
      ...userData,
      uid: userDoc.id,
      primaryRole,
      roleMetadata,
      roles: [primaryRole, ...(userData.additionalRoles || [])]
    }
  } catch (error) {
    console.error('❌ Error getting user data with correct role:', error)
    throw error
  }
}

/**
 * Sync user role - Ensures the user's role is correctly set in Firestore
 * Use this to fix users whose role is not displaying correctly
 *
 * @param {string} userId - User ID to sync
 * @param {number} correctRole - The correct role level to set
 * @param {string} adminId - Admin user ID making the change
 * @returns {Promise<object>} Result with sync status
 */
export const syncUserRole = async (userId, correctRole, adminId = null) => {
  try {
    // Obtener datos actuales del usuario
    const userDoc = await getDoc(doc(db, USERS_COLLECTION, userId))
    if (!userDoc.exists()) {
      throw new Error('Usuario no encontrado')
    }

    const userData = userDoc.data()
    // Try primaryRole first, then fall back to role field, then default to USER
    const currentRole = userData.primaryRole || userData.role || USER_ROLES.USER

    // Si el rol ya es correcto, no hacer nada
    if (currentRole === correctRole) {
      return {
        success: true,
        message: 'El rol ya es correcto',
        currentRole,
        roleMetadata: ROLE_METADATA[currentRole]
      }
    }

    // Actualizar al rol correcto
    const userRef = doc(db, USERS_COLLECTION, userId)
    await updateDoc(userRef, {
      primaryRole: correctRole,
      roleSyncedAt: serverTimestamp(),
      roleSyncedBy: adminId,
      roleHistory: arrayUnion({
        from: currentRole,
        to: correctRole,
        assignedBy: adminId,
        reason: 'Role sync - corrected by admin',
        timestamp: serverTimestamp()
      })
    })

    // Invalidar cache
    await invalidatePermissionCache(userId)

    console.log(`✅ User ${userId} role synced from ${currentRole} to ${correctRole}`)

    return {
      success: true,
      message: 'Rol sincronizado correctamente',
      previousRole: currentRole,
      newRole: correctRole,
      roleMetadata: ROLE_METADATA[correctRole]
    }
  } catch (error) {
    console.error('❌ Error syncing user role:', error)
    throw error
  }
}

/**
 * Batch sync multiple users' roles
 * Useful for fixing multiple users at once
 *
 * @param {Array<{userId: string, role: number}>} usersAndRoles - Array of user IDs and their correct roles
 * @param {string} adminId - Admin user ID making the changes
 * @returns {Promise<object>} Results with success/fail counts
 */
export const batchSyncUserRoles = async (usersAndRoles, adminId = null) => {
  try {
    let successCount = 0
    let failCount = 0
    const results = []

    for (const { userId, role } of usersAndRoles) {
      try {
        const result = await syncUserRole(userId, role, adminId)
        successCount++
        results.push({ userId, success: true, ...result })
      } catch (error) {
        failCount++
        results.push({ userId, success: false, error: error.message })
      }
    }

    return {
      success: true,
      total: usersAndRoles.length,
      successCount,
      failCount,
      results
    }
  } catch (error) {
    console.error('❌ Error in batch sync:', error)
    throw error
  }
}

